import React, { useEffect, useState } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import AutoSuggest from "../../../../../../../../components/Form/AutoSuggest";
import { Color } from "../../../../../../../../components/Form/Button";
import Input from "../../../../../../../../components/Form/Input";
import Select from "../../../../../../../../components/Form/Select";
import SelectMultiple, {
  SelectMultipleCount,
} from "../../../../../../../../components/Form/SelectMultiple";
import Modal from "../../../../../../../../components/Generic/Modal";
import useYupValidation from "../../../../../../../../hooks/useYupValidation";
import { Branch } from "../../../../../../../../models/branch";
import { StorageBox, StorageBoxDto } from "../../../../../../../../models/storage.box";
import { StorageBoxType } from "../../../../../../../../models/storage.box.type";
import { Warehouse } from "../../../../../../../../models/warehouse";

interface Props {
  open: boolean;
  hasError?: boolean;
  isLoading: boolean;
  closeModal: () => void;
  storageBoxToUpdate?: StorageBox;
  createStorageBox?: (storageBox: StorageBoxDto) => void;
  updateStorageBox?: (storageBox: StorageBoxDto, storageBoxId: number) => void;
  isFetchingModalData: boolean;
  warehouses: Warehouse[];
  branches: Branch[];
  storageBoxTypes: StorageBoxType[];
}

export const StorageBoxesPageModal = ({
  open,
  branches,
  hasError,
  warehouses,
  isLoading,
  closeModal,
  storageBoxTypes,
  createStorageBox,
  updateStorageBox,
  storageBoxToUpdate,
  isFetchingModalData,
}: Props) => {
  const { t: translate } = useTranslation();

  const [storageBoxNumber, setStorageBoxNumber] = useState<string>("");
  const [storageBoxTypeId, setStorageBoxTypeId] = useState<number>();
  const [storageBoxBranch, setStorageBoxBranch] = useState<Branch>();
  const [storageBoxWarehouses, setStorageBoxWarehouses] = useState<Warehouse[]>([]);
  const [storageBoxBelongsToWarehouseId, setStorageBoxBelongsToWarehouseId] = useState<number>();
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const [warehouseOptions, setWarehouseOptions] = useState<Warehouse[]>([]);
  const [branchOptions, setBranchOptions] = useState<Branch[]>([]);
  const [cantSetStorageBoxWarehouses, setCantSetStorageBoxWarehouses] = useState<boolean>(
    !!storageBoxToUpdate
  );

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const filterFitWarehouses = (
    warehouses: Warehouse[],
    storageBoxTypeId?: number,
    storageBoxBelongsToWarehouseId?: number
  ) => {
    if (storageBoxBelongsToWarehouseId) {
      return warehouses?.filter((warehouse) => warehouse.id === storageBoxBelongsToWarehouseId);
    }

    if (storageBoxTypeId) {
      return warehouses?.filter(
        (warehouse) =>
          !!warehouse?.sections?.find(
            (section) =>
              !!section?.storageBoxTypes?.find(
                (storageBoxType) => storageBoxType?.id === storageBoxTypeId
              )
          )
      );
    }

    return [];
  };

  const handleFitBranch = () => {
    const fitBranches: Branch[] = [];

    branches?.forEach((branch) => {
      if (
        filterFitWarehouses(
          branch?.warehouses || [],
          storageBoxTypeId,
          storageBoxBelongsToWarehouseId
        )?.length > 0
      )
        fitBranches.push(branch);
    });

    setBranchOptions(fitBranches);
    if (!!storageBoxBranch && !fitBranches.some((branch) => branch.id === storageBoxBranch.id)) {
      setStorageBoxBranch(undefined);
    }
    if (fitBranches.length === 1) setStorageBoxBranch(fitBranches[0]);
  };

  const handleFitWarehouseOptions = () => {
    setWarehouseOptions(filterFitWarehouses(warehouses, storageBoxTypeId));
  };

  const handleFitBelongsTo = () => {
    if (
      warehouseOptions.length &&
      !warehouseOptions.some((warehouse) => warehouse.id === storageBoxBelongsToWarehouseId)
    ) {
      setStorageBoxBelongsToWarehouseId(undefined);
    }
  };

  const handleFitStorageBoxWarehouses = () => {
    if (!cantSetStorageBoxWarehouses) {
      setStorageBoxWarehouses((state) =>
        state.filter((warehouse) =>
          warehouseOptions.some((warehouseOption) => warehouseOption.id === warehouse.id)
        )
      );
    }
  };

  const handleFitWarehouseOptionsWhenChangeBranch = () => {
    let warehousesToFilter = warehouses;

    if (
      storageBoxBranch &&
      storageBoxBranch.warehouses &&
      storageBoxBranch.warehouses?.length > 0
    ) {
      warehousesToFilter = storageBoxBranch.warehouses;
    }

    const warehousesFiltered = filterFitWarehouses(warehousesToFilter, storageBoxTypeId);

    setWarehouseOptions(warehousesFiltered);
  };

  useEffect(() => {
    setStorageBoxNumber(storageBoxToUpdate?.number || "");
    setStorageBoxWarehouses(storageBoxToUpdate?.warehouses || []);
    setStorageBoxBranch(storageBoxToUpdate?.branches?.[0] || undefined);
    setStorageBoxTypeId(storageBoxToUpdate?.storageBoxTypeId);
    setStorageBoxBelongsToWarehouseId(storageBoxToUpdate?.warehouseId || undefined);
  }, [storageBoxToUpdate]);

  useEffect(() => {
    handleFitBranch();
    handleFitWarehouseOptions();
  }, [storageBoxTypeId, storageBoxToUpdate]);

  useEffect(() => {
    handleFitWarehouseOptionsWhenChangeBranch();
  }, [storageBoxBranch]);

  useEffect(() => {
    handleFitBelongsTo();
    handleFitStorageBoxWarehouses();
  }, [warehouseOptions]);

  useEffect(() => {
    handleFitBranch();
    if (
      !storageBoxWarehouses?.find((warehouse) => warehouse?.id === storageBoxBelongsToWarehouseId)
    ) {
      const belongsToWarehouse = warehouses?.find(
        (warehouse) => warehouse?.id === storageBoxBelongsToWarehouseId
      );

      if (belongsToWarehouse) setStorageBoxWarehouses([belongsToWarehouse]);
    }
  }, [storageBoxBelongsToWarehouseId]);

  const schema = yup.object().shape({
    storageBoxNumber: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    storageBoxTypeId: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),

    storageBoxBelongsToWarehouseId: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const clearFields = () => {
    setStorageBoxNumber("");
    setStorageBoxWarehouses([]);
    setStorageBoxBranch(undefined);
    setStorageBoxTypeId(undefined);
    setStorageBoxBelongsToWarehouseId(undefined);
  };

  const isValid = () => {
    try {
      schema.validateSync(storageBoxesFields, { abortEarly: false });
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);
    const storageBoxType = storageBoxTypes?.find(
      (storageBoxType) => storageBoxType?.id === storageBoxTypeId
    );

    const sharedWarehousesIds: number[] = [];

    if (storageBoxType && storageBoxType?.isShareable) {
      storageBoxWarehouses?.forEach((warehouse) => {
        if (warehouse?.id) {
          sharedWarehousesIds.push(warehouse?.id);
        }
      });
    }

    const storageBoxToSave: StorageBoxDto = {
      number: storageBoxNumber,
      sharedWarehousesIds,
      belongsToWarehouseId: storageBoxBelongsToWarehouseId || 0,
      storageBoxTypeId: storageBoxTypeId ?? 0,
      isActive: storageBoxToUpdate?.isActive ?? true,
      branchesIds: storageBoxBranch ? [storageBoxBranch?.id] : [],
    };

    if (isValid()) {
      if (storageBoxToUpdate && storageBoxToUpdate.id) {
        updateStorageBox?.(storageBoxToSave, storageBoxToUpdate.id);
      } else {
        createStorageBox?.(storageBoxToSave);
      }
    }
  };

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  const storageBoxesFields = {
    storageBoxNumber,
    storageBoxTypeId,
    storageBoxBelongsToWarehouseId,
  };

  useEffect(() => {
    if (hasFormBeenSubmitted) isValid();
  }, [
    hasFormBeenSubmitted,
    ...Object.keys(storageBoxesFields).map(
      (key) => storageBoxesFields[key as keyof typeof storageBoxesFields]
    ),
  ]);

  return (
    <Modal
      open={open}
      handleClose={() => {
        clearFields();
        closeModal();
      }}
      title={storageBoxToUpdate ? translate("editStorageBox") : translate("newStorageBox")}
      disableEnforceFocus
      data-cy="newStorageBox"
      dataCyTitle="newStorageBoxTitle"
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        customColorCancel: Color.TEXT_INFO,
        dataCySave: "submitChangesButton",
        textSave: storageBoxToUpdate ? translate("save") : translate("create"),
        loading: isLoading,
        saveDisabled: isLoading || errors.length > 0,
        onSave: () => handleSubmit(),
      }}
    >
      <Grid container direction="row" alignItems="flex-start" spacing={2}>
        <Grid item xs={12} md={6}>
          <Input
            data-cy="storageBoxNumber"
            placeholder={`${translate("typeNumber")}...`}
            label={translate("number")}
            error={fieldHasError("storageBoxNumber")}
            errorMessage={getErrorByField("storageBoxNumber")}
            required
            value={storageBoxNumber}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setStorageBoxNumber(event.target.value)
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Select
            data-cy="storageBoxType"
            placeholder={`${translate("select")}...`}
            label={translate("type")}
            disabled={isFetchingModalData}
            error={fieldHasError("storageBoxTypeId")}
            errorMessage={getErrorByField("storageBoxTypeId")}
            required
            value={storageBoxTypeId}
            onChange={(event: SelectChangeEvent<any>) => {
              setCantSetStorageBoxWarehouses(false);
              setStorageBoxTypeId(Number(event.target.value));
            }}
          >
            {storageBoxTypes?.map((storageBoxType) => (
              <MenuItem value={storageBoxType?.id} key={storageBoxType?.id}>
                {storageBoxType?.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} md={6}>
          <AutoSuggest
            label={translate("branch")}
            placeholder={`${translate("select")}...`}
            dataCy="storageBoxBranch"
            disabled={isFetchingModalData || !storageBoxTypeId || branchOptions.length === 0}
            value={storageBoxBranch}
            inputValue={storageBoxBranch?.name}
            getOptionLabel={(option: Branch) => option.name || ""}
            getOptionSelected={(option?: Branch, value?: Branch) => option?.id === value?.id}
            options={branchOptions}
            onChange={(branch: Branch) => {
              setCantSetStorageBoxWarehouses(false);
              setStorageBoxBranch(branch);
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Select
            data-cy="belongsToWarehouse"
            placeholder={`${translate("select")}...`}
            label={translate("belongsTo")}
            disabled={isFetchingModalData || !storageBoxTypeId || warehouseOptions.length === 0}
            error={fieldHasError("storageBoxBelongsToWarehouseId")}
            errorMessage={getErrorByField("storageBoxBelongsToWarehouseId")}
            required
            value={storageBoxBelongsToWarehouseId}
            onChange={(event: SelectChangeEvent<any>) => {
              setCantSetStorageBoxWarehouses(false);
              setStorageBoxBelongsToWarehouseId(Number(event.target.value));
            }}
          >
            {warehouseOptions &&
              warehouseOptions?.length > 0 &&
              warehouseOptions?.map((warehouse) => (
                <MenuItem value={warehouse?.id} key={warehouse?.id}>
                  {warehouse?.name}
                </MenuItem>
              ))}
          </Select>
        </Grid>
        {!storageBoxTypeId ||
        (storageBoxTypeId &&
          storageBoxTypes.find((storageBoxType) => storageBoxType?.id === storageBoxTypeId)
            ?.isShareable) ? (
          <Grid
            item
            container
            direction="row"
            alignItems="flex-end"
            spacing={1}
            xs={12}
            sm={12}
            md={12}
            lg={12}
          >
            <Grid item xs={10} sm={10} md={6} lg={6}>
              <SelectMultiple
                label={translate("sharedWith")}
                dataCy="sharedWarehouses"
                name="sharedWarehouses"
                disabled={!storageBoxTypeId || !warehouseOptions?.length}
                onChange={(event: SelectChangeEvent<any>) => {
                  setCantSetStorageBoxWarehouses(false);
                  if (event.target.value.includes(0) || event.target.value.includes(-1)) return;

                  const warehousesSelected = warehouseOptions?.filter((warehouse) =>
                    event.target.value.includes(warehouse.id)
                  );

                  if (storageBoxBelongsToWarehouseId) {
                    const warehouseBelongsTo = warehouseOptions?.find(
                      (warehouse) => warehouse?.id === storageBoxBelongsToWarehouseId
                    );

                    if (
                      warehouseBelongsTo &&
                      !warehousesSelected?.find(
                        (warehouseSelected) =>
                          warehouseSelected.id === storageBoxBelongsToWarehouseId
                      )
                    ) {
                      warehousesSelected?.push(warehouseBelongsTo);
                    }
                  }

                  setStorageBoxWarehouses(warehousesSelected);
                }}
                value={storageBoxWarehouses?.map((warehouse) => Number(warehouse?.id))}
                onAddAll={() => setStorageBoxWarehouses(warehouseOptions)}
                onRemoveAll={() => {
                  if (storageBoxBelongsToWarehouseId) {
                    const warehouse = warehouseOptions?.find(
                      (warehouse) => warehouse?.id === storageBoxBelongsToWarehouseId
                    );

                    if (warehouse) {
                      return setStorageBoxWarehouses([warehouse]);
                    }
                  }

                  return setStorageBoxWarehouses([]);
                }}
                renderInternalCount={false}
                items={
                  warehouseOptions?.map((warehouse) => ({
                    key: Number(warehouse.id),
                    label: warehouse.name,
                  })) || []
                }
                selecteds={
                  storageBoxWarehouses?.map((warehouse) => ({
                    key: Number(warehouse.id),
                    label: warehouse.name,
                  })) || []
                }
              />
            </Grid>
            <Grid item xs={2} sm={2} md={2} lg={2}>
              <SelectMultipleCount
                items={warehouseOptions?.map((warehouse) => ({
                  key: Number(warehouse.id),
                  label: warehouse.name,
                }))}
                selecteds={
                  storageBoxWarehouses?.map((warehouse) => ({
                    key: Number(warehouse.id),
                    label: warehouse.name,
                  })) || []
                }
              />
            </Grid>
          </Grid>
        ) : null}
      </Grid>
    </Modal>
  );
};
